import { useState, type JSX } from 'react';
import { api } from '../lib/api.ts';
import type { ChatItem, ToolItem } from '../lib/chat.ts';
import { Markdown } from '../lib/markdown.tsx';

const TOOL_GLYPH: Record<string, string> = {
  read: 'R',
  write: 'W',
  edit: 'E',
  'search replace': 'E',
  ls: 'L',
  glob: 'F',
  grep: 'F',
  shell: '$',
  bash: '$',
  terminal: '$',
  delete: 'X',
  todo: 'T',
  web: 'W',
};

export function Item({
  item,
  sessionId,
  enter = false,
}: {
  item: ChatItem;
  sessionId: string;
  enter?: boolean;
}): JSX.Element | null {
  switch (item.type) {
    case 'user':
      return (
        <div className="row row-user">
          <div className="bubble bubble-user">
            {item.images.length > 0 && (
              <div className="bubble-images">
                {item.images.map((img) => (
                  <img
                    key={img.id}
                    src={api.imageUrl(sessionId, img.id)}
                    alt=""
                    className="bubble-image"
                    loading="lazy"
                  />
                ))}
              </div>
            )}
            {item.text ? <span>{item.text}</span> : null}
          </div>
        </div>
      );

    case 'assistant':
      return (
        <div className={`row row-assistant${enter ? ' row-enter' : ''}`}>
          <div className="bubble bubble-assistant">
            <Markdown text={item.text} />
            {item.streaming && <span className="caret" aria-hidden="true" />}
          </div>
        </div>
      );

    case 'tool':
      return <ToolRow items={[item]} />;

    case 'video':
      return (
        <div className={`row row-assistant${enter ? ' row-enter' : ''}`}>
          <figure className="demo">
            <video
              className="demo-video"
              src={api.videoUrl(sessionId, item.videoId)}
              controls
              playsInline
              // Metadata only: a chat can hold several demos, and the phone
              // should not pull megabytes for takes nobody scrolled to.
              preload="metadata"
              {...(item.width && item.height
                ? { width: item.width, height: item.height }
                : {})}
            />
            {item.caption ? <figcaption className="demo-caption">{item.caption}</figcaption> : null}
          </figure>
        </div>
      );

    case 'shot':
      return (
        <div className={`row row-assistant${enter ? ' row-enter' : ''}`}>
          <figure className="demo">
            {/* A crop of a control is often small; the link is how a phone gets
                at the full-size pixels without a viewer of our own. */}
            <a href={api.imageUrl(sessionId, item.imageId)} target="_blank" rel="noreferrer">
              <img
                className="demo-shot"
                src={api.imageUrl(sessionId, item.imageId)}
                alt={item.caption || 'Screenshot the agent took'}
                loading="lazy"
                {...(item.width && item.height ? { width: item.width, height: item.height } : {})}
              />
            </a>
            {item.caption ? <figcaption className="demo-caption">{item.caption}</figcaption> : null}
          </figure>
        </div>
      );

    case 'note':
      return (
        <div className={`note ${item.level === 'error' ? 'note-error' : ''}`}>
          <pre>{item.text}</pre>
        </div>
      );
  }
}

export function ToolRow({ items }: { items: ToolItem[] }): JSX.Element {
  const [open, setOpen] = useState(false);
  const latest = items[items.length - 1]!;
  const running = items.some((item) => item.state === 'running');
  const failed = items.some((item) => item.state === 'fail');
  const count = items.length;
  const label = `${count} tool ${count === 1 ? 'call' : 'calls'}`;
  const state = running ? 'running' : failed ? 'fail' : 'ok';

  return (
    <div className={`tool tool-${state}`}>
      <button
        type="button"
        className="tool-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={latest.summary ? `${label}: ${latest.summary}` : label}
      >
        <span className="tool-row-glyphs" aria-hidden="true">
          {items.map((item) => (
            <span key={item.id} className="tool-glyph">
              {TOOL_GLYPH[item.tool] ?? '*'}
            </span>
          ))}
        </span>
        <span className="tool-summary">{latest.summary}</span>
        <span className="tool-row-count">{count}</span>
        <span className="tool-state" aria-hidden="true">
          {running ? <span className="spinner" /> : failed ? '!' : ''}
        </span>
      </button>
      {open && (
        <div className="tool-row-items">
          {items.map((item) => (
            <ToolChip key={item.id} item={item} nested />
          ))}
        </div>
      )}
    </div>
  );
}

function ToolChip({ item, nested = false }: { item: ToolItem; nested?: boolean }): JSX.Element {
  const [open, setOpen] = useState(false);
  const hasDetail = Boolean(item.detail);
  return (
    <div className={`tool tool-${item.state}${nested ? ' tool-nested' : ''}`}>
      <button
        type="button"
        className="tool-head"
        onClick={() => hasDetail && setOpen((v) => !v)}
        aria-expanded={hasDetail ? open : undefined}
      >
        <span className="tool-glyph" aria-hidden="true">
          {TOOL_GLYPH[item.tool] ?? '*'}
        </span>
        <span className="tool-name">{item.tool}</span>
        <span className="tool-summary">{item.summary}</span>
        <span className="tool-state" aria-hidden="true">
          {item.state === 'running' ? <span className="spinner" /> : item.state === 'ok' ? '' : '!'}
        </span>
      </button>
      {open && item.detail && <pre className="tool-detail">{item.detail}</pre>}
    </div>
  );
}
